import autoBind from "auto-bind";
import { isEmpty, isEqual, uniqWith } from "lodash";
import PropTypes from "prop-types";
import qs from "qs";
import React from "react";

import config from "../../config";
import joinUrlPath from "../url/joinUrlPath";
import { store } from "../../redux/configureStore";
import { getIsWeb } from "../platformHelper";
import ApiHelper from "./CoreHelpers/ApiHelper";
import {
  ACCESS_DENIED_REASON,
  getAccessDeniedReasonFromApiError,
  getPermissionsFromApiError,
  isAccessDeniedApiError
} from "../api/accessDeniedError";
import * as DataInterceptors from "./DataInterceptor/";
import { FetchTabWithOptionsParams } from "./DataInterceptor/DefaultDataInterceptor";
import {
  buildUniversalTableUrlSettings,
  mergeUniversalTableSeededSettings,
  parseUniversalTableUrlSettings,
  UNIVERSAL_TABLE_DEFAULT_PAGINATION
} from "./buildUniversalTableUrlSettings";
import {
  LOCKED_SETTINGS_QUERY_KEY,
  normalizeLockedSettings,
  preserveLockedRecord
} from "./lockedSettings";

export default class UniversalTableCore extends React.Component {
  static defaultPagination = UNIVERSAL_TABLE_DEFAULT_PAGINATION;

  constructor(props) {
    super(props);

    const defaultSettings = props.defaultSettings || {};

    const DataInterceptorClass =
      DataInterceptors[
        `${props.type}${UniversalTableCore.dataInterceptorSuffix}`
      ];

    this.dataInterceptor = new DataInterceptorClass(
      props.locale,
      props.authToken,
      props.options
    );

    autoBind.react(this);

    let excludedSettings = [];

    ["filters", "search"].forEach(key => {
      if (defaultSettings[key]) {
        excludedSettings.push(...Object.keys(defaultSettings[key]));
      }
    });

    const lockedSettings = normalizeLockedSettings(
      defaultSettings.lockedSettings
    );

    this.state = {
      UniversalTable: {
        config: null,
        data: null,
        currentTab: null,
        configAccessDenied: false,
        configAccessDeniedPermissions: null,
        configAccessDeniedReason: null,
        dataLoading: true,
        configLoading: true,
        isFetchingNextPage: false,
        pagination: {
          ...UniversalTableCore.defaultPagination,
          ...defaultSettings.pagination
        },
        sortBy: defaultSettings.sortBy || {
          field: null,
          direction: "asc"
        },
        search: defaultSettings.search || {},
        filters: defaultSettings.filters || {},
        excludedSettings,
        lockedSettings,
        columnsFontSize: {},
        customTabsById: {},
        getTableState: this.getTableState,
        getExcludedSettings: this.getExcludedSettings,
        getLockedSettings: this.getLockedSettings,
        setTableState: this.setTableState,
        setSorting: this.setSorting,
        setSearch: this.setSearch,
        setFilters: this.setFilters,
        getColumns: this.getColumns,
        getFilterColumns: this.getFilterColumns,
        getAllAvailableColumns: this.getAllAvailableColumns,
        reFetchAfterSettingsChange: this.fetchData,
        setPaginationState: this.setPaginationState
      }
    };
  }

  static propTypes = {
    type: PropTypes.oneOf(["Default", "TransactionHistory"]),
    refreshOn: PropTypes.arrayOf(PropTypes.string),
    useUrlSettings: PropTypes.bool,
    useSavedSettings: PropTypes.bool,
    usePagination: PropTypes.bool,
    hideLoadMore: PropTypes.bool,
    columnLengthSmall: PropTypes.number,
    columnLengthExtraSmall: PropTypes.number,
    defaultSettings: PropTypes.shape({
      search: PropTypes.object,
      filters: PropTypes.object,
      lockedSettings: PropTypes.arrayOf(PropTypes.string),
      sortBy: PropTypes.shape({
        field: PropTypes.string,
        direction: PropTypes.string
      }),
      pagination: PropTypes.shape({
        current: PropTypes.number,
        rowsPerPage: PropTypes.number
      })
    }),
    initialSettings: PropTypes.object
  };

  static defaultProps = {
    type: "Default",
    useUrlSettings: false,
    useSavedSettings: false,
    usePagination: true,
    hideLoadMore: false,
    columnLengthSmall: 13,
    columnLengthExtraSmall: 20,
    defaultSettings: {},
    initialSettings: null
  };

  static dataInterceptorSuffix = "DataInterceptor";

  async componentDidMount(): void {
    const { refreshOn } = this.props;
    await this.initialize();

    if (!this.getTableState().configAccessDenied) {
      await this.fetchData();
    }

    if (refreshOn && refreshOn.length) {
      this.unsubscribeFromReduxActions = store.subscribe(() => {
        const lastAction = store.getState().lastAction;

        if (refreshOn.indexOf(lastAction.type) !== -1) {
          this.refresh();
        }
      });
    }
  }

  componentWillUnmount(): void {
    if (this.unsubscribeFromReduxActions) {
      this.unsubscribeFromReduxActions();
    }
  }

  shouldComponentUpdate(
    nextProps: Readonly<P>,
    nextState: Readonly<S>,
    nextContext: any
  ): boolean {
    return (
      !this.state.data ||
      this.state.data.next !== nextState.data.next ||
      this.state.dataLoading !== nextState.dataLoading ||
      this.state.configLoading !== nextState.configLoading ||
      this.state.configAccessDenied !== nextState.configAccessDenied ||
      this.state.configAccessDeniedPermissions !==
        nextState.configAccessDeniedPermissions ||
      this.state.configAccessDeniedReason !==
        nextState.configAccessDeniedReason ||
      this.state.isFetchingNextPage !== nextState.isFetchingNextPage
    );
  }

  async initialize() {
    return new Promise(async (resolve, reject) => {
      const {
        resourceId,
        defaultSettings,
        initialSettings,
        fetchConfigParams,
        translate
      } = this.props;

      let config;

      try {
        config = await this.dataInterceptor.fetchConfig(
          resourceId,
          defaultSettings,
          fetchConfigParams,
          translate
        );
      } catch (error) {
        if (isAccessDeniedApiError(error)) {
          this.setTableState({
            configAccessDenied: true,
            configAccessDeniedPermissions: getPermissionsFromApiError(error),
            configAccessDeniedReason: getAccessDeniedReasonFromApiError(error),
            configLoading: false,
            dataLoading: false
          });
          resolve();
          return;
        }

        throw error;
      }

      if (!config || !Array.isArray(config.tabs)) {
        this.setTableState({
          configAccessDenied: true,
          configAccessDeniedPermissions: null,
          configAccessDeniedReason: ACCESS_DENIED_REASON.PERMISSION,
          configLoading: false,
          dataLoading: false
        });
        resolve();
        return;
      }

      config = this.transformConfig(config);

      let currentTab = config.tabs.length
        ? config.tabs.find(tab => tab.is_default) || config.tabs[0]
        : null;

      let settingsFromUrl = {};

      if (this.shouldUseUrlSettings()) {
        settingsFromUrl = this.getSettingsFromUrl();
      }

      const seededSettings = mergeUniversalTableSeededSettings(
        initialSettings,
        settingsFromUrl
      );

      const initialTabId =
        seededSettings.currentTabId ?? defaultSettings?.currentTabId;

      if (initialTabId) {
        const resolvedTabId = Number(initialTabId);
        seededSettings.currentTabId = resolvedTabId;
        const tabFromSettings = config.tabs.find(
          tab => tab.id === resolvedTabId
        );

        if (config.tabs.length && tabFromSettings) {
          currentTab = tabFromSettings;
        }
      }

      let savedSettings = {};

      if (this.shouldUseSavedSettings()) {
        savedSettings = this.getSavedSettings(
          this.createCacheKey({ tableId: config.id, tableName: config.name })
        );
      }

      currentTab = await this.withResolvedCustomTabSettings(currentTab);

      if (!currentTab) {
        this.setTableState({
          configAccessDenied: true,
          configAccessDeniedPermissions: null,
          configAccessDeniedReason: ACCESS_DENIED_REASON.PERMISSION,
          configLoading: false,
          dataLoading: false
        });
        resolve();
        return;
      }

      const lockedSettings = normalizeLockedSettings(
        settingsFromUrl[LOCKED_SETTINGS_QUERY_KEY] ||
          this.getTableState().lockedSettings
      );

      if (this.getTableState().configAccessDenied) {
        this.setTableState({
          config,
          currentTab,
          currentTabId: currentTab.id,
          configLoading: false,
          dataLoading: false,
          ...seededSettings,
          ...savedSettings,
          lockedSettings
        });
        resolve();
        return;
      }

      this.setTableState(
        {
          config,
          currentTab,
          currentTabId: currentTab.id,
          configLoading: false,
          ...seededSettings,
          ...savedSettings,
          lockedSettings
        },
        () => {
          resolve();
        }
      );
    });
  }

  async fetchData(
    tabId,
    callback = () => {},
    futureState = null,
    params: FetchTabWithOptionsParams
  ) {
    const { columnLengthSmall, columnLengthExtraSmall } = this.props;

    this.setTableState({
      dataLoading: true
    });

    const { resourceId, fetchDataParams, hasPermission } = this.props;

    const { currentTab, configAccessDenied } =
      futureState || this.getTableState();

    if (configAccessDenied || !currentTab) {
      this.setTableState({
        dataLoading: false
      });
      callback();
      return;
    }

    let initialData = {
      count: 0,
      next: null,
      previous: null,
      results: []
    };

    if (
      !currentTab?.is_premium ||
      !currentTab?.permissions ||
      hasPermission(currentTab.permissions)
    ) {
      const customTabSettings = await this.resolveCustomTabSettings(currentTab);

      if (this.getTableState().configAccessDenied) {
        this.setTableState({
          dataLoading: false
        });
        callback();
        return;
      }

      const customApiEndpoint =
        currentTab?.apiEndpoint || customTabSettings?.apiEndpoint;
      const dataParams = {
        ...(params || fetchDataParams),
        ...(customApiEndpoint ? { url: customApiEndpoint } : {})
      };

      try {
        const fetchedData = await this.dataInterceptor.fetchTabWithOptions(
          resourceId,
          tabId || currentTab.id,
          futureState || this.getTableState(),
          dataParams
        );

        if (fetchedData) {
          initialData = fetchedData;
        }
      } catch (error) {
        if (isAccessDeniedApiError(error)) {
          this.setTableState({
            configAccessDenied: true,
            configAccessDeniedPermissions: getPermissionsFromApiError(error),
            configAccessDeniedReason: getAccessDeniedReasonFromApiError(error),
            dataLoading: false
          });
          callback();
          return;
        }

        throw error;
      }
    }

    let columnsFontSize = {};

    if (
      getIsWeb() &&
      initialData &&
      initialData.results &&
      initialData.results.length
    ) {
      const columns = this.getAllAvailableColumns();
      columns
        .filter(
          column =>
            column.values_type === "string" &&
            Object.keys(initialData.results[0]).indexOf(
              column.attribute_name
            ) !== -1
        )
        .forEach(column => {
          for (let row of initialData.results) {
            const cell = row[column.attribute_name];

            if (typeof cell === "undefined" || cell === null) {
              continue;
            }

            if (cell.length >= columnLengthExtraSmall) {
              columnsFontSize[column.attribute_name] = "extra-small";
              break;
            } else if (cell.length >= columnLengthSmall) {
              columnsFontSize[column.attribute_name] = "small";
            }
          }
        });
    }

    this.setTableState({
      data: initialData,
      dataLoading: false,
      columnsFontSize
    });

    callback();
  }

  async fetchNextPage() {
    this.setIsFetchingNextPage(true);
    const {
      data: { next: nextPageUrl }
    } = this.getTableState();

    const nextPageData = await this.dataInterceptor.fetchNextPage(nextPageUrl);
    const { results, next } = nextPageData;

    this.setTableState(prevState => ({
      data: {
        results: [...prevState.data.results, ...results],
        next
      }
    }));

    this.setIsFetchingNextPage(false);
  }

  async refresh() {
    this.dataInterceptor.clearCache();
    this.setTableState({
      customTabsById: {},
      configAccessDenied: false,
      configAccessDeniedPermissions: null,
      configAccessDeniedReason: null
    });
    await this.fetchData();
  }

  getColumns() {
    const columnPairs = this.getColumnPairs();

    if (!columnPairs.length) {
      return [];
    }

    let columns = [];

    columnPairs.forEach(columnPair => {
      columns.push(columnPair.csv_data_column_first);
      columns.push(columnPair.csv_data_column_second);
    });

    return columns.filter(column => !!column);
  }

  getFilterColumns() {
    const tableColumns = [];
    const columnPairs = this.getColumnPairs();

    columnPairs.forEach(columnPair => {
      tableColumns.push(columnPair.csv_data_column_first);
      tableColumns.push(columnPair.csv_data_column_second);
    });

    const fromPairs = tableColumns.filter(column => !!column);
    const { currentTab } = this.getTableState();
    const additionalFilters = (currentTab?.additional_filters || []).filter(
      Boolean
    );

    return [...fromPairs, ...additionalFilters];
  }

  getAllAvailableColumns() {
    const { config } = this.getTableState();

    let columns = [];

    if (!config) {
      return columns;
    }

    config.tabs.forEach(tab => {
      tab.column_pairs.forEach(columnPair => {
        ["csv_data_column_first", "csv_data_column_second"].map(columnKey => {
          const column = columnPair[columnKey];
          if (column) {
            columns.push(column);
          }
        });
      });
      (tab.additional_filters || []).forEach(column => {
        if (column) {
          columns.push(column);
        }
      });
    });

    return uniqWith(columns, isEqual);
  }

  getColumnPairs() {
    const { currentTab } = this.getTableState();

    if (!currentTab) {
      return [];
    }

    return currentTab.column_pairs;
  }

  getTabApiEndpoint(tab) {
    if (!tab?.custom_api_endpoint) {
      return null;
    }

    const apiUrl = config.apiUrl.replace(":locale", this.props.locale);
    return joinUrlPath(apiUrl, tab.custom_api_endpoint);
  }

  getCustomTabConfig(tabConfig, fallbackTab) {
    if (!tabConfig || typeof tabConfig !== "object") {
      return null;
    }

    if (Array.isArray(tabConfig.tabs) && tabConfig.tabs.length) {
      return (
        tabConfig.tabs.find(tab => tab.id === fallbackTab?.id) ||
        tabConfig.tabs.find(tab => tab.is_default) ||
        tabConfig.tabs[0]
      );
    }

    return tabConfig;
  }

  applyCustomTabAccessDeniedState(cachedSettings) {
    return new Promise(resolve => {
      this.setTableState(
        {
          configAccessDenied: true,
          configAccessDeniedPermissions: cachedSettings.permissions,
          configAccessDeniedReason: cachedSettings.reason
        },
        resolve
      );
    });
  }

  async resolveCustomTabSettings(tab) {
    if (!tab?.custom_api_endpoint) {
      return null;
    }

    const { customTabsById } = this.getTableState();

    if (customTabsById[tab.id]) {
      const cached = customTabsById[tab.id];

      if (cached.accessDenied) {
        await this.applyCustomTabAccessDeniedState(cached);
      }

      return cached;
    }

    const apiEndpoint = this.getTabApiEndpoint(tab);

    let tabConfig = null;
    try {
      tabConfig = await this.dataInterceptor.makeRequest({
        url: joinUrlPath(apiEndpoint, "config")
      });
    } catch (error) {
      console.error(error);

      if (isAccessDeniedApiError(error)) {
        const deniedSettings = {
          accessDenied: true,
          apiEndpoint,
          permissions: getPermissionsFromApiError(error),
          reason: getAccessDeniedReasonFromApiError(error)
        };

        await new Promise(resolve => {
          this.setTableState(
            state => ({
              configAccessDenied: true,
              configAccessDeniedPermissions: deniedSettings.permissions,
              configAccessDeniedReason: deniedSettings.reason,
              customTabsById: {
                ...state.customTabsById,
                [tab.id]: deniedSettings
              }
            }),
            resolve
          );
        });

        return deniedSettings;
      }

      return null;
    }

    const resolvedCustomTabConfig = this.getCustomTabConfig(tabConfig, tab);
    const customTabSettings = {
      apiEndpoint,
      tabConfig: resolvedCustomTabConfig || {},
      columnPairs: resolvedCustomTabConfig?.column_pairs || []
    };

    await new Promise(resolve => {
      this.setTableState(
        state => ({
          configAccessDenied: false,
          configAccessDeniedPermissions: null,
          configAccessDeniedReason: null,
          customTabsById: {
            ...state.customTabsById,
            [tab.id]: customTabSettings
          }
        }),
        resolve
      );
    });

    return customTabSettings;
  }

  async withResolvedCustomTabSettings(tab) {
    if (!tab) {
      return tab;
    }

    const customTabSettings = await this.resolveCustomTabSettings(tab);

    if (!customTabSettings || customTabSettings.accessDenied) {
      return tab;
    }

    return {
      ...tab,
      ...customTabSettings.tabConfig,
      apiEndpoint: customTabSettings.apiEndpoint,
      column_pairs: customTabSettings.columnPairs
    };
  }

  setIsFetchingNextPage(isFetchingNextPage) {
    this.setTableState({
      isFetchingNextPage
    });
  }

  setTableState(newState, callback) {
    this.setState(
      state => ({
        UniversalTable: {
          ...state.UniversalTable,
          ...(typeof newState === "function"
            ? newState(state.UniversalTable)
            : newState)
        }
      }),
      (...params) => {
        if (callback) {
          callback(...params);
        }

        if (this.canUpdateSavedSettings()) {
          this.updateSavedSettings(
            this.createCacheKey({
              tableId: this.state.UniversalTable?.config?.id,
              tableName: this.state.UniversalTable?.config?.name
            }),
            {
              sortBy: this.state.UniversalTable.sortBy,
              pagination: this.state.UniversalTable.pagination,
              search: this.state.UniversalTable.search,
              filters: this.state.UniversalTable.filters,
              currentTabId: this.state.UniversalTable.currentTabId
            }
          );
        }

        this.appendSettingsToUrl();
      }
    );
  }

  canUpdateSavedSettings() {
    if (!this.shouldUseSavedSettings()) {
      return false;
    }

    const { search, filters, sortBy, pagination } = this.getTableState();

    if (
      !isEmpty(search) ||
      !isEmpty(filters) ||
      !isEqual(pagination, UniversalTableCore.defaultPagination) ||
      sortBy.field !== null
    ) {
      return true;
    }
  }

  appendSettingsToUrl() {
    if (!this.shouldUseUrlSettings()) {
      return;
    }

    const {
      currentTabId,
      search,
      filters,
      sortBy,
      pagination,
      lockedSettings
    } = this.getTableState();
    const baseUrl = window.location.origin + window.location.pathname;
    let settingsToAppend = buildUniversalTableUrlSettings({
      currentTabId,
      search,
      filters,
      sortBy,
      pagination
    });

    if (!isEmpty(lockedSettings)) {
      settingsToAppend = {
        ...settingsToAppend,
        [LOCKED_SETTINGS_QUERY_KEY]: lockedSettings
      };
    }

    history.replaceState(
      null,
      "",
      baseUrl + "?" + qs.stringify(settingsToAppend)
    );
  }

  getSettingsFromUrl() {
    const settingsFromUrl = parseUniversalTableUrlSettings(
      window.location.search
    );

    if (settingsFromUrl[LOCKED_SETTINGS_QUERY_KEY]) {
      settingsFromUrl[LOCKED_SETTINGS_QUERY_KEY] = normalizeLockedSettings(
        settingsFromUrl[LOCKED_SETTINGS_QUERY_KEY]
      );
    }

    return settingsFromUrl;
  }

  shouldUseUrlSettings() {
    return this.props.useUrlSettings && getIsWeb();
  }

  shouldUseSavedSettings() {
    return this.props.useSavedSettings;
  }

  getSavedSettings(cacheKey) {
    throw new Error("Not implemented");
  }

  saveSettings(cacheKey, settings) {
    throw new Error("Not implemented");
  }

  updateSavedSettings(cacheKey, settings) {
    throw new Error("Not implemented");
  }

  createCacheKey({ tableId, tableName }) {
    return `${tableId}${tableName}`;
  }

  setSorting(newSorting) {
    this.setTableState(
      oldState => ({
        sortBy: newSorting,
        pagination: {
          ...oldState.pagination,
          current: 1
        }
      }),
      () => {
        this.fetchData().catch(() => {});
      }
    );
  }

  setSearch(newSearch) {
    this.setTableState(
      oldState => ({
        search: preserveLockedRecord(
          newSearch,
          oldState.search,
          oldState.lockedSettings
        ),
        pagination: {
          ...oldState.pagination,
          current: 1
        }
      }),
      () => {
        this.fetchData().catch(() => {});
      }
    );
  }

  setFilters(newFilters) {
    this.setTableState(
      oldState => ({
        filters: preserveLockedRecord(
          newFilters,
          oldState.filters,
          oldState.lockedSettings
        ),
        pagination: {
          ...oldState.pagination,
          current: 1
        }
      }),
      () => {
        this.fetchData().catch(() => {});
      }
    );
  }

  setPaginationState(newPagination) {
    this.setTableState({
      dataLoading: true
    });

    setTimeout(() => {
      this.fetchData(
        null,
        () => {
          this.setTableState(state => ({
            pagination: {
              ...state.pagination,
              ...newPagination
            }
          }));
        },
        {
          ...this.getTableState(),
          pagination: {
            ...this.getTableState().pagination,
            ...newPagination
          }
        }
      );
    }, 100);
  }

  setTab(id, initializing = false) {
    const { config } = this.getTableState();

    const newTab = config.tabs.find(tab => tab.id === id);

    this.setTableState({
      dataLoading: true,
      configAccessDenied: false,
      configAccessDeniedPermissions: null,
      configAccessDeniedReason: null,
      sortBy: {
        field: null,
        direction: "asc"
      }
    });

    setTimeout(
      async () => {
        const tabWithResolvedCustomConfig = await this.withResolvedCustomTabSettings(
          newTab
        );

        if (this.getTableState().configAccessDenied) {
          this.setTableState({
            dataLoading: false,
            currentTabId: id,
            currentTab: newTab
          });
          return;
        }

        this.fetchData(
          id,
          () => {
            this.setTableState({
              currentTabId: id,
              currentTab: tabWithResolvedCustomConfig
            });
          },
          {
            ...this.getTableState(),
            currentTab: tabWithResolvedCustomConfig
          }
        ).catch(e => {
          console.error(e);
        });
      },
      !initializing ? 500 : 0
    );
  }

  getTableState() {
    return this.state.UniversalTable;
  }

  getExcludedSettings() {
    return this.getTableState().excludedSettings;
  }

  getLockedSettings() {
    return this.getTableState().lockedSettings;
  }

  transformConfig(config) {
    if (!config || !Array.isArray(config.tabs)) {
      return config;
    }

    const { transformTypes } = this.props;

    const applyColumnTransform = column => {
      if (!column) {
        return;
      }

      const valuesType = column.values_type;
      const filterData = column.filter_data;

      if (transformTypes && transformTypes[valuesType]) {
        column.values_type = transformTypes[valuesType];
      }

      if (
        ApiHelper.getIsFilterableByRange(column) &&
        filterData &&
        !isNaN(filterData.min) &&
        !isNaN(filterData.max)
      ) {
        column.filter_data = {
          min: Math.floor(filterData.min * 100) / 100,
          max: Math.ceil(filterData.max * 100) / 100
        };
      }
    };

    config.tabs.forEach(tab => {
      tab.column_pairs.forEach(columnPair => {
        ["csv_data_column_first", "csv_data_column_second"].forEach(
          columnKey => {
            applyColumnTransform(columnPair[columnKey]);
          }
        );
      });
      (tab.additional_filters || []).forEach(applyColumnTransform);
    });

    return config;
  }
}
